import React, { useState, useEffect } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContextLazy'
import { EnvelopeIcon, ArrowLeftIcon } from '@heroicons/react/24/outline'

// Rate limiting constants
const RESET_ATTEMPT_KEY = 'password_reset_attempts'
const RATE_LIMIT = 3
const TIME_WINDOW = 5 * 60 * 1000 // 5 minutes in milliseconds (reduced from 1 hour)

export const ForgotPasswordForm: React.FC = () => {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const { resetPassword } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // Check for error state from redirect (e.g., expired link)
  useEffect(() => {
    const state = location.state as { error?: string; fromExpiredLink?: boolean } | null
    if (state?.error) {
      setError(state.error)
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, '', location.pathname)
    }
  }, [location])

  // Check if user can request password reset
  const canRequestReset = (email: string): { allowed: boolean; waitTime?: number } => {
    const attempts = JSON.parse(localStorage.getItem(RESET_ATTEMPT_KEY) || '{}')
    const userAttempts = (attempts[email] || []) as number[]
    
    // Filter attempts within the last hour
    const now = Date.now()
    const recentAttempts = userAttempts.filter(
      (timestamp: number) => now - timestamp < TIME_WINDOW
    )
    
    if (recentAttempts.length >= RATE_LIMIT) {
      // Calculate wait time
      const oldestAttempt = Math.min(...recentAttempts)
      const waitTime = Math.ceil((TIME_WINDOW - (now - oldestAttempt)) / 60000)
      return { allowed: false, waitTime }
    }
    
    return { allowed: true }
  }

  // Track password reset attempt
  const trackResetAttempt = (email: string): void => {
    const attempts = JSON.parse(localStorage.getItem(RESET_ATTEMPT_KEY) || '{}')
    const userAttempts = attempts[email] || []
    // Keep only recent attempts to prevent localStorage bloat
    const now = Date.now()
    const recentAttempts = userAttempts.filter(
      (timestamp: number) => now - timestamp < TIME_WINDOW
    )
    attempts[email] = [...recentAttempts, now]
    localStorage.setItem(RESET_ATTEMPT_KEY, JSON.stringify(attempts))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    // Check rate limit before making request
    const resetCheck = canRequestReset(email)
    if (!resetCheck.allowed) {
      setError(`Too many password reset attempts. Please wait ${resetCheck.waitTime} minute${resetCheck.waitTime! > 1 ? 's' : ''} before trying again.`)
      setLoading(false)
      return
    }

    // Get the appropriate redirect URL based on environment
    const redirectUrl = window.location.origin + '/reset-password'
    
    const { error } = await resetPassword(email, redirectUrl)
    
    if (error) {
      // Handle rate limit error from server
      if (error.message?.includes('over_email_send_rate_limit') || 
          error.message?.includes('rate limit') ||
          error.message?.includes('429')) {
        // Track this attempt even though it failed
        trackResetAttempt(email)
        setError('Password reset email limit reached. Please wait 1 hour before trying again.')
      } else {
        setError(error.message)
      }
      setLoading(false)
    } else {
      // Track successful attempt
      trackResetAttempt(email)
      setSuccess(true)
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      
      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Logo and Branding */}
        <div className="text-center flex flex-col items-center">
          <Link to="/" className="block -ml-6">
            <img 
              className="h-32 w-auto mb-6 cursor-pointer hover:opacity-90 transition-opacity" 
              src="/assets/Brands/PCM Logo White.png" 
              alt="PCM" 
            />
          </Link>
          <h2 className="text-4xl font-bold text-white mb-2">
            Password Reset
          </h2>
          <p className="text-base text-gray-300">
            We'll send you instructions to reset your password
          </p>
          <div className="w-20 h-1 bg-amber-400 mx-auto mt-6"></div>
        </div>
        
        {/* Form Card */}
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl p-8">
          {!success ? (
            <>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">
                Enter your email address
              </h3>
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email-address"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      className="appearance-none relative block w-full pl-10 pr-3 py-3 bg-gray-50 border border-gray-300 placeholder-gray-400 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 focus:bg-white transition-all duration-200 sm:text-sm"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                {error && (
                  <div className="rounded-lg bg-red-50 border border-red-200 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-red-800">
                          {error}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {loading ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </div>
                    ) : (
                      'Send Reset Instructions'
                    )}
                  </button>
                </div>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center text-sm font-medium text-amber-600 hover:text-amber-500 transition-colors"
                  >
                    <ArrowLeftIcon className="h-4 w-4 mr-1" />
                    Back to login
                  </Link>
                </div>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Check your email</h3>
              <p className="text-sm text-gray-600 mb-6">
                We've sent password reset instructions to <strong>{email}</strong>
              </p>
              <p className="text-xs text-gray-500 mb-6">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center text-sm font-medium text-amber-600 hover:text-amber-500 transition-colors"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-1" />
                Back to login
              </Link>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="text-center text-sm text-gray-400">
          <p>
            Need assistance? Contact us at{' '}
            <a href="mailto:support@pcmfinancial.ca" className="text-amber-400 hover:text-amber-300 transition-colors">
              support@pcmfinancial.ca
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}